import { CSSProperties, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "@entry/navigation";
import styled from "styled-components";

import { futureMoneyIconRound, halfmoreFutureMoneyGlass, manifestFutureMoney } from "@assets/images";
import { PARTNER_LOGO_KEYS } from "@constants";
import { AnalyticsEvent, Routes } from "@ctypes";
import { entryColors, entryFont, entryMotion, prefersReducedMotion } from "@entry/tokens";
import { EntryCta, EntryTitle } from "@entry/ui";
import { Translations, i18n } from "@i18n";
import { Analytics } from "@services";
import { Screen, ScreenColumn, ScreenCta, useCtaSpacer, useEntryPageAnalytics } from "./shared";

const PARTNER_LOGOS = {
  halfmoreFutureMoneyGlass,
  manifestFutureMoney,
};

type LocationState = {
  partnerName?: string;
  loginMethod?: string;
  phoneNumber?: string;
};

type Stage = "hidden" | "visible";

const { easing, slideDistance } = entryMotion;

const GlassCard = styled.div`
  width: 100%;
  max-width: 420px;
  border-radius: 20px;
  background: rgba(244, 241, 242, 0.08);
  border: 1px solid ${entryColors.glassBorder};
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  padding: 36px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
`;

const Logo = styled.img<{ $isPartner: boolean }>`
  width: ${({ $isPartner }) => ($isPartner ? "109px" : "60px")};
  height: 60px;
`;

const Header = styled.p`
  margin: 0;
  font-family: ${entryFont.body};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${entryColors.highlight};
`;

const Body = styled.p`
  margin: 0;
  font-family: ${entryFont.body};
  font-size: 15px;
  line-height: 1.55;
  color: ${entryColors.textSecondary};
`;

const Closing = styled.p`
  margin: 4px 0 0;
  font-family: ${entryFont.body};
  font-size: 14px;
  font-weight: 500;
  color: ${entryColors.textPrimary};
`;

export const PartnerExistingAccountScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState) || {};
  const { partnerName, loginMethod, phoneNumber } = state;

  const [reduced] = useState(
    () =>
      prefersReducedMotion() ||
      (typeof document !== "undefined" && document.visibilityState === "hidden"),
  );
  const [stage, setStage] = useState<Stage>(reduced ? "visible" : "hidden");
  const navigated = useRef(false);
  const { ctaRef, ctaSpacerHeight } = useCtaSpacer();

  useEntryPageAnalytics(Routes.PartnerExistingAccount);

  useEffect(() => {
    if (reduced) return;
    const raf = window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setStage("visible")),
    );
    return () => window.cancelAnimationFrame(raf);
  }, [reduced]);

  const logoKey = partnerName ? PARTNER_LOGO_KEYS[partnerName] : undefined;
  const logoSrc = logoKey ? PARTNER_LOGOS[logoKey] : futureMoneyIconRound;

  const bodyText = partnerName
    ? i18n.t(Translations.PartnerExistingAccountBodyPartner, { partnerName })
    : i18n.t(Translations.PartnerExistingAccountBodyGeneric);

  const closingText =
    loginMethod === "PHONE_OTP_SENT"
      ? i18n.t(Translations.PartnerExistingAccountClosingOtpSent)
      : i18n.t(Translations.PartnerExistingAccountClosingDefault);

  const handleContinue = () => {
    if (navigated.current) return;
    navigated.current = true;

    Analytics.track(AnalyticsEvent.PartnerExistingAccountPrimaryCTAPressed);

    if (loginMethod === "PHONE_OTP_SENT" && phoneNumber) {
      navigate(Routes.PhoneLoginVerification, { state: { phoneNumber }, replace: true });
    } else if (loginMethod === "PHONE") {
      navigate(Routes.PhoneLoginInput, { replace: true });
    } else {
      navigate(Routes.Login, { replace: true });
    }
  };

  const contentStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    opacity: stage === "visible" ? 1 : 0,
    transform: `translateY(${stage === "hidden" ? slideDistance : 0}px)`,
    transition: reduced ? "none" : `transform 600ms ${easing}, opacity 800ms ${easing}`,
  };

  const ctaStyle: CSSProperties = {
    opacity: stage === "visible" ? 1 : 0,
    transform: `translateY(${stage === "hidden" ? 20 : 0}px)`,
    transition: reduced
      ? "none"
      : `transform 500ms ${easing} 200ms, opacity 600ms ${easing} 200ms`,
  };

  return (
    <Screen>
      <ScreenColumn>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={contentStyle}>
            <GlassCard>
              <Logo src={logoSrc} $isPartner={!!logoKey} alt="" />
              <Header>{i18n.t(Translations.PartnerExistingAccountHeader)}</Header>
              <EntryTitle as="h2" $align="center" $size={28}>
                {i18n.t(Translations.PartnerExistingAccountTitle)}
              </EntryTitle>
              <Body>{bodyText}</Body>
              <Closing>{closingText}</Closing>
            </GlassCard>
          </div>
        </div>

        <div aria-hidden="true" style={{ height: ctaSpacerHeight, flexShrink: 0 }} />
        <ScreenCta ref={ctaRef} style={ctaStyle}>
          <EntryCta type="button" $variant="terracotta" onClick={handleContinue}>
            {i18n.t(Translations.PartnerExistingAccountPrimaryCTA)}
          </EntryCta>
        </ScreenCta>
      </ScreenColumn>
    </Screen>
  );
};
